import React, { useEffect, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Select,
  Spinner,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const RELATIONSHIP_OPTIONS = [
  "Mother",
  "Father",
  "Spouse / Partner",
  "Child",
  "Sibling",
  "Grandparent",
  "Other loved one",
];

function normalizeMessage(value) {
  return (value || "").trim();
}

async function getToken() {
  const user = getAuth().currentUser;

  if (!user) {
    throw new Error("Authentication required.");
  }

  return user.getIdToken();
}

async function apiFetch(path, options = {}) {
  const token = await getToken();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    },
    body: options.body ? options.body : undefined,
  });

  if (response.status === 401 || response.status === 403) {
    throw new Error("Your session is no longer valid. Please sign in again.");
  }

  if (response.status === 404) {
    throw new Error("That CareCircle or conversation was not found.");
  }

  const text = await response.text();
  const payload = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new Error(
      payload?.error ||
        payload?.message ||
        "The request could not be completed."
    );
  }

  return payload;
}

function mostRecentConversation(conversations) {
  return [...conversations].sort(
    (a, b) =>
      new Date(b.updatedAt || b.createdAt) -
      new Date(a.updatedAt || a.createdAt)
  )[0];
}

function SelenseHealthMemory() {
  const isMounted = useRef(true);

  const [careCircles, setCareCircles] = useState([]);
  const [selectedCircleId, setSelectedCircleId] = useState("");
  const [selectedCircle, setSelectedCircle] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [identityDraft, setIdentityDraft] = useState("");
  const [relationshipDraft, setRelationshipDraft] = useState("");
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [isCreatingCircle, setIsCreatingCircle] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [memoryActions, setMemoryActions] = useState({});

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const loadCareCircles = async () => {
      try {
        setStatus("loading");
        setError("");

        const circles = await apiFetch("/api/care-circles");

        if (!isMounted.current) return;

        const availableCircles = Array.isArray(circles) ? circles : [];

        const activeCircles = availableCircles.filter(
          (circle) => circle.status === "active"
        );

        setCareCircles(activeCircles);

        setSelectedCircleId(
          activeCircles.length > 0 ? activeCircles[0]._id : ""
        );

        setStatus("ready");
      } catch (err) {
        if (!isMounted.current) return;

        setError(err.message || "Unable to load CareCircle data.");
        setStatus("error");
      }
    };

    loadCareCircles();
  }, []);

  useEffect(() => {
    const circle =
      careCircles.find((item) => item._id === selectedCircleId) || null;

    setSelectedCircle(circle);
  }, [careCircles, selectedCircleId]);

  useEffect(() => {
    if (!selectedCircleId) {
      setConversation(null);
      setMessages([]);
      return;
    }

    const loadConversation = async () => {
      try {
        setIsLoadingMessages(true);
        setError("");

        const conversations = await apiFetch(
          `/api/care-circles/${selectedCircleId}/conversations`
        );

        if (!isMounted.current) return;

        let activeConversation =
          Array.isArray(conversations) && conversations.length > 0
            ? mostRecentConversation(conversations)
            : null;

        if (!activeConversation) {
          activeConversation = await apiFetch(
            `/api/care-circles/${selectedCircleId}/conversations`,
            {
              method: "POST",
              body: JSON.stringify({
                title: "Care memory conversation",
                context: "Persistent health memory conversation",
              }),
            }
          );
        }

        if (!isMounted.current) return;

        setConversation(activeConversation);

        const existingMessages = await apiFetch(
          `/api/care-circles/${selectedCircleId}/conversations/${activeConversation._id}/messages`
        );

        if (!isMounted.current) return;

        setMessages(
          Array.isArray(existingMessages) ? existingMessages : []
        );
      } catch (err) {
        if (!isMounted.current) return;

        setError(err.message || "Unable to load conversation history.");
        setConversation(null);
        setMessages([]);
      } finally {
        if (isMounted.current) {
          setIsLoadingMessages(false);
        }
      }
    };

    loadConversation();
  }, [selectedCircleId]);

  const handleCreateCircle = async (event) => {
    event.preventDefault();

    const name = normalizeMessage(identityDraft);
    const relationship = normalizeMessage(relationshipDraft);

    if (!name || !relationship || isCreatingCircle) return;

    setIsCreatingCircle(true);
    setError("");

    try {
      const createdCircle = await apiFetch("/api/care-circles", {
        method: "POST",
        body: JSON.stringify({
          name,
          relationship,
        }),
      });

      if (!isMounted.current) return;

      setCareCircles((previous) => [createdCircle, ...previous]);
      setSelectedCircleId(createdCircle._id);
      setIdentityDraft("");
      setRelationshipDraft("");
    } catch (err) {
      if (isMounted.current) {
        setError(err.message || "Unable to create the CareCircle.");
      }
    } finally {
      if (isMounted.current) {
        setIsCreatingCircle(false);
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmed = normalizeMessage(draft);

    if (!trimmed || isSending || !selectedCircle || !conversation) {
      return;
    }

    setIsSending(true);
    setError("");

    try {
      const aiPayload = await apiFetch(
        `/api/care-circles/${selectedCircle._id}/conversations/${conversation._id}/ai`,
        {
          method: "POST",
          body: JSON.stringify({
            message: trimmed,
          }),
        }
      );

      if (!isMounted.current) return;

      setDraft("");

      setMessages((previous) => [
        ...previous,
        aiPayload.userMessage,
        aiPayload.assistantMessage,
      ]);
    } catch (err) {
      if (isMounted.current) {
        setError(err.message || "Selense was unable to respond.");
      }
    } finally {
      if (isMounted.current) {
        setIsSending(false);
      }
    }
  };

  const handleSaveMemory = async (message) => {
    if (
      !message._id ||
      message.role !== "user" ||
      memoryActions[message._id]
    ) {
      return;
    }

    setMemoryActions((previous) => ({
      ...previous,
      [message._id]: { status: "saving" },
    }));

    setError("");

    try {
      const memory = await apiFetch(
        `/api/care-circles/${selectedCircle._id}/memories`,
        {
          method: "POST",
          body: JSON.stringify({
            type: "health_event",
            value: message.content,
            sourceConversationId: conversation._id,
            sourceMessageId: message._id,
          }),
        }
      );

      if (!isMounted.current) return;

      try {
        await apiFetch(
          `/api/care-circles/${selectedCircle._id}/memories/${memory._id}/confirm`,
          {
            method: "PATCH",
          }
        );

        if (!isMounted.current) return;

        setMemoryActions((previous) => ({
          ...previous,
          [message._id]: {
            status: "confirmed",
            memoryId: memory._id,
          },
        }));
      } catch (confirmError) {
        if (!isMounted.current) return;

        setMemoryActions((previous) => ({
          ...previous,
          [message._id]: {
            status: "confirmation_error",
            memoryId: memory._id,
          },
        }));

        setError(
          confirmError.message ||
            "The memory was created, but Selense could not confirm it."
        );
      }
    } catch (err) {
      if (isMounted.current) {
        setMemoryActions((previous) => ({
          ...previous,
          [message._id]: { status: "error" },
        }));

        setError(
          err.message || "Unable to save this to health memory."
        );
      }
    }
  };

  const hasCareCircle = Boolean(selectedCircle);

  return (
    <Box
      bg="white"
      borderRadius="2xl"
      border="1px solid"
      borderColor="gray.200"
      boxShadow="lg"
      p={{ base: 4, md: 6 }}
      mb={6}
    >
      <HStack
        justify="space-between"
        align="start"
        mb={4}
        wrap="wrap"
      >
        <Box>
          <Heading as="h3" size="md" color="teal.700">
            Selense
          </Heading>

          <Text fontSize="sm" color="gray.600" mt={1}>
            A calm place to talk through what is happening and keep your
            conversations together.
          </Text>
        </Box>

        {careCircles.length > 1 && (
          <Box minW={{ base: "100%", md: "220px" }}>
            <Select
              value={selectedCircleId}
              onChange={(event) =>
                setSelectedCircleId(event.target.value)
              }
              size="sm"
              aria-label="Choose who you are caring for"
            >
              {careCircles.map((circle) => (
                <option key={circle._id} value={circle._id}>
                  {circle.name}
                </option>
              ))}
            </Select>
          </Box>
        )}
      </HStack>

      {status === "loading" && (
        <Box
          py={6}
          display="flex"
          justifyContent="center"
        >
          <Spinner color="teal.500" />
        </Box>
      )}

      {error && (
        <Alert status="error" borderRadius="lg" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {!hasCareCircle && status === "ready" && (
        <Box
          bg="teal.50"
          borderRadius="xl"
          p={5}
          mb={4}
          border="1px solid"
          borderColor="teal.100"
        >
          <Heading as="h4" size="sm" color="teal.900">
            Who are you caring for?
          </Heading>

          <Text
            color="teal.800"
            mt={2}
            fontSize="sm"
            lineHeight="1.6"
          >
            Start with one person you love. Selense will help you keep
            the important parts of their health story together.
          </Text>

          <form onSubmit={handleCreateCircle}>
            <VStack spacing={4} align="stretch" mt={5}>
              <FormControl>
                <FormLabel color="teal.900">
                  Their name
                </FormLabel>

                <Input
                  value={identityDraft}
                  onChange={(event) =>
                    setIdentityDraft(event.target.value)
                  }
                  placeholder="e.g. Mum, Dad, Aisha..."
                  isDisabled={isCreatingCircle}
                  bg="white"
                />
              </FormControl>

              <FormControl>
                <FormLabel color="teal.900">
                  Your relationship
                </FormLabel>

                <Select
                  value={relationshipDraft}
                  onChange={(event) =>
                    setRelationshipDraft(event.target.value)
                  }
                  placeholder="Choose a relationship"
                  isDisabled={isCreatingCircle}
                  bg="white"
                >
                  {RELATIONSHIP_OPTIONS.map((relationship) => (
                    <option
                      key={relationship}
                      value={relationship}
                    >
                      {relationship}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <Button
                type="submit"
                colorScheme="teal"
                size="lg"
                isLoading={isCreatingCircle}
                loadingText="Starting their health memory..."
                isDisabled={
                  !identityDraft.trim() ||
                  !relationshipDraft ||
                  isCreatingCircle
                }
              >
                Start their health memory
              </Button>

              <Text
                fontSize="xs"
                color="teal.700"
                lineHeight="1.5"
              >
                You can add more health information later. For now,
                just start with the person.
              </Text>
            </VStack>
          </form>
        </Box>
      )}

      {hasCareCircle && (
        <Box
          bg="gray.50"
          borderRadius="xl"
          p={4}
          mb={4}
        >
          <Text fontWeight="bold" color="gray.700">
            {selectedCircle.name}
          </Text>

          {selectedCircle.relationship && (
            <Text fontSize="sm" color="gray.600">
              {selectedCircle.relationship}
            </Text>
          )}
        </Box>
      )}

      {hasCareCircle && (
        <>
          <Box
            bg="gray.50"
            borderRadius="xl"
            p={4}
            minH="180px"
            mb={4}
          >
            {isLoadingMessages ? (
              <Box
                display="flex"
                justifyContent="center"
                py={4}
              >
                <Spinner
                  size="sm"
                  color="teal.500"
                />
              </Box>
            ) : messages.length === 0 ? (
              <Box>
                <Heading
                  as="h4"
                  size="sm"
                  color="gray.700"
                >
                  Let&apos;s start with them.
                </Heading>

                <Text
                  color="gray.600"
                  mt={2}
                >
                  Tell me a little about the person you&apos;re caring
                  for. You can start anywhere; there&apos;s no need to
                  tell me everything at once.
                </Text>
              </Box>
            ) : (
              <VStack
                align="stretch"
                spacing={3}
              >
                {messages.map((message, index) => (
                  <Box
                    key={`${
                      message._id ||
                      message.createdAt ||
                      index
                    }-${index}`}
                    alignSelf={
                      message.role === "user"
                        ? "flex-end"
                        : "flex-start"
                    }
                    maxW="85%"
                    bg={
                      message.role === "user"
                        ? "teal.500"
                        : "white"
                    }
                    color={
                      message.role === "user"
                        ? "white"
                        : "gray.700"
                    }
                    borderRadius="lg"
                    px={3}
                    py={2}
                    border="1px solid"
                    borderColor={
                      message.role === "user"
                        ? "teal.500"
                        : "gray.200"
                    }
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      mb={1}
                    >
                      {message.role === "user"
                        ? "You"
                        : "Selense"}
                    </Text>

                    <Text whiteSpace="pre-wrap">
                      {message.content}
                    </Text>

                    {message.role === "user" &&
                      message._id && (
                        <HStack mt={2} spacing={2}>
                          {!memoryActions[message._id] && (
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme="teal"
                              onClick={() =>
                                handleSaveMemory(message)
                              }
                            >
                              Save to health memory
                            </Button>
                          )}

                          {memoryActions[message._id]?.status ===
                            "saving" && (
                            <Text
                              fontSize="xs"
                              color="gray.500"
                            >
                              Saving...
                            </Text>
                          )}

                          {memoryActions[message._id]?.status ===
                            "confirmed" && (
                            <Text
                              fontSize="xs"
                              color="teal.600"
                              fontWeight="medium"
                            >
                              ✓ Saved to health memory.
                            </Text>
                          )}

                          {memoryActions[message._id]?.status ===
                            "confirmation_error" && (
                            <Text
                              fontSize="xs"
                              color="orange.600"
                              fontWeight="medium"
                            >
                              Memory saved, but confirmation could not
                              be completed.
                            </Text>
                          )}
                        </HStack>
                      )}
                  </Box>
                ))}
              </VStack>
            )}
          </Box>

          <Divider mb={4} />

          <form onSubmit={handleSubmit}>
            <FormControl>
              <Textarea
                value={draft}
                onChange={(event) =>
                  setDraft(event.target.value)
                }
                placeholder="Tell Selense what's on your mind..."
                aria-label="Tell Selense what is on your mind"
                minH="110px"
                resize="vertical"
                isDisabled={
                  isSending || isLoadingMessages
                }
              />
            </FormControl>

            <HStack
              justify="space-between"
              mt={4}
              wrap="wrap"
            >
              <Text
                fontSize="sm"
                color="gray.500"
              >
                You can continue where you left off whenever
                you&apos;re ready.
              </Text>

              <Button
                type="submit"
                colorScheme="teal"
                isLoading={isSending}
                loadingText="Thinking..."
                isDisabled={
                  !draft.trim() ||
                  isSending ||
                  isLoadingMessages
                }
              >
                Ask Selense
              </Button>
            </HStack>
          </form>
        </>
      )}
    </Box>
  );
}

export default SelenseHealthMemory;
